import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api, {
	Project,
	ReleaseChannel,
	SortOrder,
	UploadProjectBuildCommand,
	UserProductStatus,
	UpdateProjectStoreDetailsCommand,
	UpdateProjectBuildNameCommand,
	GetAllProjectsQuerySortField,
} from 'api';

interface CustomProject extends Project {
	loading: boolean;
}

interface InitialState {
	filter: UserProductStatus | undefined;
	sortOrder: SortOrder;
	projects: Project[] | null;
	selectedProject: CustomProject | null;
	sorterIndex?: number;
}

const initialState: InitialState = {
	filter: undefined, // means all
	sortOrder: SortOrder.ASC,
	projects: null,
	selectedProject: null,
	sorterIndex: undefined,
};
export const getProjects = createAsyncThunk(
	'projects/getProjects',
	async (data: {
		sortField: GetAllProjectsQuerySortField | undefined;
		sortOrder: SortOrder | undefined;
	}) => {
		return api.getProjects(data.sortField, data.sortOrder);
	},
);

export const createProject = createAsyncThunk(
	'projects/createProject',
	async (name: string) => {
		return api.initProject({ name });
	},
);

export const getProject = createAsyncThunk(
	'projects/getProject',
	async (id: number) => {
		return api.getProjectInfo(id);
	},
);
export const getProjectProduct = createAsyncThunk(
	'projects/getProjectProduct',
	async (projectId: number) => {
		return api.getProjectProduct(projectId);
	},
);

export const getProjectBuilds = createAsyncThunk(
	'projects/getProjectBuilds',
	async (data: { projectId: number; releaseChannel?: ReleaseChannel }) => {
		return api.getProjectBuilds(data.projectId, data.releaseChannel);
	},
);

export const updateProjectStoreDetails = createAsyncThunk(
	'projects/updateProjectStoreDetails',
	async (data: UpdateProjectStoreDetailsCommand) => {
		if (data.projectId) {
			return api.updateProjectStoreDetails(data.projectId, data);
		}
	},
);

export const getLatestProjectBuild = createAsyncThunk(
	'projects/getLatestProjectBuild',
	async (data: { projectId: number; releaseChannel?: ReleaseChannel }) => {
		return api.getLatestProjectBuild(data.projectId, data.releaseChannel);
	},
);

export const releaseProjectOnStore = createAsyncThunk(
	'projects/releaseProjectOnStore',
	async (projectId: number) => {
		return api.releaseProjectOnStore(projectId);
	},
);

export const getProjectStatus = createAsyncThunk(
	'projects/getProjectStatus',
	async (projectId: number) => {
		return api.getProjectStatus(projectId);
	},
);

export const submitProjectForReview = createAsyncThunk(
	'projects/submitProjectForReview',
	async (projectId: number, { rejectWithValue }) => {
		try {
			return api.submitProjectForReview(projectId);
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export type ProjectBuildCommand = {
	projectId: number;
	buildName: string;
} & UploadProjectBuildCommand;

export type DeleteProjectBuildCommand = {
	projectId: number;
	buildId: string;
};

export type ProjectBuildNameCommand = {
	projectId: number;
} & UpdateProjectBuildNameCommand;

export const uploadProjectBuild = createAsyncThunk(
	'project/build/uploadProjectBuild',
	async (data: ProjectBuildCommand) => {
		return api.uploadProjectBuild(data.projectId, data);
	},
);

export const updateProjectBuildName = createAsyncThunk(
	'project/build/updatProjectBuild',
	async (data: ProjectBuildNameCommand) => {
		if (data.buildName) {
			return api.updateProjectBuildName(data.projectId, data);
		}
	},
);

export const deleteProjectBuild = createAsyncThunk(
	'project/build/deleteProjectBuild',
	async (data: DeleteProjectBuildCommand) => {
		if (data.buildId) {
			return api.deleteProjectBuild(data.buildId, data.projectId);
		}
	},
);

const projectsSlice = createSlice({
	name: 'projects',
	initialState,
	reducers: {
		updateSorterIndex: (state, action) => {
			state.sorterIndex = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getProject.fulfilled, (state, action) => {
			state.selectedProject = { ...action.payload, loading: false };
		});
		builder.addCase(getProject.pending, (state, action) => {
			if (action.meta.arg === state.selectedProject?.id) {
				state.selectedProject = { ...state.selectedProject, loading: false };
			} else {
				state.selectedProject = { ...state.selectedProject, loading: true };
			}
		});
		builder.addCase(getProjects.fulfilled, (state, action) => {
			if (action.payload.totalCount && action.payload.items) {
				state.projects = action.payload.items;
			}
		});
	},
});

const { reducer, actions } = projectsSlice;
const { updateSorterIndex } = actions;
export { updateSorterIndex };

export default reducer;
